/**
 * Shopify Product Form Interceptor — Fixed Version
 * 
 * FIXES:
 * - No longer relies on hardcoded form ID (works on any product page)
 * - Waits for DOM + Seal Subscriptions widget to fully render
 * - More robust selectors for radio buttons and interval select
 * - Better error logging
 */

(function () {
  // ─── CONFIG ────────────────────────────────────────────────────────────────
  const API_BASE_URL      = "https://kellymarieevolv.vercel.app"; // 🔁 Replace with your API URL
  const BUTTON_TEXT       = "Subscribe Now";
  const BUTTON_COLOR      = "#f0a500";
  const BUTTON_TEXT_COLOR = "#ffffff";
  // ───────────────────────────────────────────────────────────────────────────

  // Wait for DOM + Seal widget to render (it loads async)
  function waitForForm(callback, attempts) {
    attempts = attempts || 0;
    if (attempts > 40) {
      console.warn("[Interceptor] Form not found after waiting. Check your product page.");
      return;
    }

    // Match ANY add-to-cart form on the page (works regardless of template ID)
    const form = document.querySelector("form[action*='/cart/add']");

    // Also wait for Seal Subscriptions radio buttons to be injected
    const sealRadios = form && form.querySelectorAll("input[name^='subs_type']");

    if (form && sealRadios && sealRadios.length > 0) {
      console.log("[Interceptor] Form and Seal widget found. Attaching...");
      callback(form);
    } else {
      console.log("[Interceptor] Waiting for form/widget... attempt", attempts + 1);
      setTimeout(function () { waitForForm(callback, attempts + 1); }, 250);
    }
  }

  // ── Inject Styles ──────────────────────────────────────────────────────────
  const style = document.createElement("style");
  style.textContent = `
    #subs-custom-btn {
      display: none;
      width: 100%;
      padding: 14px;
      background: ${BUTTON_COLOR};
      color: ${BUTTON_TEXT_COLOR};
      border: none;
      border-radius: 6px;
      font-size: 15px;
      font-weight: 700;
      letter-spacing: 0.5px;
      cursor: pointer;
      margin-top: 10px;
      text-transform: uppercase;
      transition: opacity 0.2s;
    }
    #subs-custom-btn:hover { opacity: 0.88; }

    #subs-modal-overlay {
      display: none;
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.55);
      z-index: 99999;
      align-items: center;
      justify-content: center;
    }
    #subs-modal-overlay.open { display: flex; }

    #subs-modal {
      background: #fff;
      border-radius: 12px;
      padding: 36px 32px 28px;
      width: 90%;
      max-width: 420px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.2);
      font-family: inherit;
      position: relative;
      animation: subs-slide-in 0.25s ease;
    }
    @keyframes subs-slide-in {
      from { transform: translateY(24px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    #subs-modal h2 {
      margin: 0 0 6px;
      font-size: 20px;
      font-weight: 700;
      color: #111;
    }
    #subs-modal .subs-subtitle {
      margin: 0 0 24px;
      font-size: 14px;
      color: #666;
    }
    #subs-modal .subs-field { margin-bottom: 16px; }
    #subs-modal label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 6px;
      color: #333;
    }
    #subs-modal input {
      width: 100%;
      padding: 10px 14px;
      border: 1.5px solid #ddd;
      border-radius: 8px;
      font-size: 15px;
      outline: none;
      transition: border-color 0.2s;
      box-sizing: border-box;
    }
    #subs-modal input:focus { border-color: #111; }
    #subs-modal input.subs-input-error { border-color: #e53e3e; }
    #subs-modal .subs-error-msg {
      font-size: 12px;
      color: #e53e3e;
      margin-top: 4px;
      display: none;
    }
    #subs-modal .subs-error-msg.show { display: block; }
    #subs-modal .subs-row { display: flex; gap: 12px; }
    #subs-modal .subs-row .subs-field { flex: 1; }
    #subs-modal-submit {
      width: 100%;
      padding: 13px;
      background: ${BUTTON_COLOR};
      color: ${BUTTON_TEXT_COLOR};
      border: none;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
      margin-top: 8px;
      transition: opacity 0.2s;
      text-transform: uppercase;
    }
    #subs-modal-submit:hover { opacity: 0.88; }
    #subs-modal-submit:disabled { opacity: 0.6; cursor: not-allowed; }
    #subs-modal-close {
      position: absolute;
      top: 14px;
      right: 16px;
      background: none;
      border: none;
      font-size: 22px;
      cursor: pointer;
      color: #999;
      line-height: 1;
      padding: 0;
    }
    #subs-modal-close:hover { color: #111; }
    #subs-modal .subs-secure {
      text-align: center;
      font-size: 12px;
      color: #999;
      margin-top: 14px;
      margin-bottom: 0;
    }
  `;
  document.head.appendChild(style);

  // ── Inject Modal HTML (once, outside waitForForm) ──────────────────────────
  const overlay = document.createElement("div");
  overlay.id = "subs-modal-overlay";
  overlay.innerHTML = `
    <div id="subs-modal" role="dialog" aria-modal="true" aria-labelledby="subs-modal-title">
      <button id="subs-modal-close" aria-label="Close">&times;</button>
      <h2 id="subs-modal-title">Almost there!</h2>
      <p class="subs-subtitle">Enter your details to set up your subscription.</p>
      <div class="subs-row">
        <div class="subs-field">
          <label for="subs-first-name">First name</label>
          <input type="text" id="subs-first-name" placeholder="Jane" autocomplete="given-name">
          <div class="subs-error-msg" id="subs-first-name-err">Required</div>
        </div>
        <div class="subs-field">
          <label for="subs-last-name">Last name</label>
          <input type="text" id="subs-last-name" placeholder="Doe" autocomplete="family-name">
          <div class="subs-error-msg" id="subs-last-name-err">Required</div>
        </div>
      </div>
      <div class="subs-field">
        <label for="subs-email">Email address</label>
        <input type="email" id="subs-email" placeholder="jane@example.com" autocomplete="email">
        <div class="subs-error-msg" id="subs-email-err">Enter a valid email address</div>
      </div>
      <button id="subs-modal-submit">Continue to payment</button>
      <p class="subs-secure">🔒 Secure checkout powered by Paystack</p>
    </div>
  `;
  document.body.appendChild(overlay);

  // ── Modal helpers ──────────────────────────────────────────────────────────
  var pendingData = null;

  function openModal() {
    overlay.classList.add("open");
    document.getElementById("subs-first-name").focus();
  }
  function closeModal() {
    overlay.classList.remove("open");
    clearErrors();
  }
  function clearErrors() {
    ["subs-first-name", "subs-last-name", "subs-email"].forEach(function (id) {
      document.getElementById(id).classList.remove("subs-input-error");
    });
    ["subs-first-name-err", "subs-last-name-err", "subs-email-err"].forEach(function (id) {
      document.getElementById(id).classList.remove("show");
    });
  }
  function showFieldError(inputId, errId) {
    document.getElementById(inputId).classList.add("subs-input-error");
    document.getElementById(errId).classList.add("show");
  }
  function validateModal() {
    clearErrors();
    var valid      = true;
    var firstName  = document.getElementById("subs-first-name").value.trim();
    var lastName   = document.getElementById("subs-last-name").value.trim();
    var email      = document.getElementById("subs-email").value.trim();
    var emailOk    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!firstName) { showFieldError("subs-first-name", "subs-first-name-err"); valid = false; }
    if (!lastName)  { showFieldError("subs-last-name",  "subs-last-name-err");  valid = false; }
    if (!emailOk)   { showFieldError("subs-email",      "subs-email-err");      valid = false; }

    return valid ? { firstName: firstName, lastName: lastName, email: email } : null;
  }

  overlay.addEventListener("click", function (e) { if (e.target === overlay) closeModal(); });
  document.getElementById("subs-modal-close").addEventListener("click", closeModal);
  document.addEventListener("keydown", function (e) { if (e.key === "Escape") closeModal(); });

  document.getElementById("subs-modal-submit").addEventListener("click", async function () {
    var fields = validateModal();
    if (!fields || !pendingData) return;

    var btn = document.getElementById("subs-modal-submit");
    btn.disabled    = true;
    btn.textContent = "Processing...";

    try {
      var payload = {
        email:           fields.email,
        first_name:      fields.firstName,
        last_name:       fields.lastName,
        amount:          pendingData.amountInCents,
        plan_name:       pendingData.planName,
        interval:        "monthly",
        selling_plan_id: pendingData.planSellingPlanId,
      };

      console.log("[Interceptor] Calling /setup with:", payload);

      var res  = await fetch(API_BASE_URL + "/setup", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      var data = await res.json();

      console.log("[Interceptor] /setup response:", data);

      if (!res.ok) throw new Error(data?.error || "Setup failed");
      if (!data.payment_url) throw new Error("No payment URL returned");

      window.location.href = data.payment_url;

    } catch (err) {
      console.error("[Interceptor] API error:", err);
      alert("Something went wrong: " + err.message);
      btn.disabled    = false;
      btn.textContent = "Continue to payment";
    }
  });

  // ── Main init (runs after form + Seal widget are ready) ───────────────────
  waitForForm(function (form) {

    // ── Create custom button ─────────────────────────────────────────────────
    var customBtn = document.createElement("button");
    customBtn.id          = "subs-custom-btn";
    customBtn.type        = "button";
    customBtn.textContent = BUTTON_TEXT;

    var submitContainer = form.querySelector(".product-form__quantity-submit");
    if (submitContainer) {
      submitContainer.parentNode.insertBefore(customBtn, submitContainer.nextSibling);
    } else {
      form.appendChild(customBtn);
    }

    var addToCartBtn = form.querySelector(".product-form__item--submit");
    var buyItNowWrap = form.querySelector(".product-form__buynow");

    // ── Toggle buttons ───────────────────────────────────────────────────────
    function updateButtons() {
      var checked = form.querySelector("input[name^='subs_type']:checked");
      var isSub   = checked && checked.dataset && checked.dataset.type === "subscription";

      console.log("[Interceptor] Selected type:", checked?.dataset?.type, "| isSub:", isSub);

      if (addToCartBtn) addToCartBtn.style.display = isSub ? "none" : "";
      if (buyItNowWrap) buyItNowWrap.style.display  = isSub ? "none" : "";
      customBtn.style.display = isSub ? "block" : "none";
    }

    // Attach to all radio buttons (Seal uses its own click handlers so also listen on the container)
    form.querySelectorAll("input[name^='subs_type']").forEach(function (r) {
      r.addEventListener("change", updateButtons);
    });

    // Seal Subscriptions uses div clicks, not just radio change — so also watch clicks on the whole widget
    var sealContainer = form.querySelector(".sealsubs-container");
    if (sealContainer) {
      sealContainer.addEventListener("click", function () {
        setTimeout(updateButtons, 50); // small delay so Seal updates the radio first
      });
    }

    updateButtons(); // run on load

    // ── Custom button click ──────────────────────────────────────────────────
    customBtn.addEventListener("click", function () {
      var intervalSelect    = form.querySelector("select[name='subs_interval'], select.sls-select");
      var selectedOption    = intervalSelect && intervalSelect.options[intervalSelect.selectedIndex];
      var planName          = selectedOption ? selectedOption.text  : null;
      var planSellingPlanId = selectedOption ? selectedOption.value : null;
      var variantSelect     = form.querySelector("select[name='id']");
      var variantId         = variantSelect ? variantSelect.value : null;

      console.log("[Interceptor] Plan:", planName, "| Selling plan ID:", planSellingPlanId, "| Variant:", variantId);

      // Parse product JSON from data-product attribute
      var productData = null;
      try {
        var el = form.closest("[data-product]") || form.querySelector("[data-product]") || document.querySelector("[data-product]");
        if (el) productData = JSON.parse(el.dataset.product);
      } catch (e) {
        console.warn("[Interceptor] Could not parse product data:", e);
      }

      var amountInCents = 0;
      if (productData && productData.variants) {
        var variant = productData.variants.find(function (v) {
          return String(v.id) === String(variantId);
        });
        if (variant) {
          var allocation = variant.selling_plan_allocations && variant.selling_plan_allocations.find(function (a) {
            return String(a.selling_plan_id) === String(planSellingPlanId);
          });
          amountInCents = (allocation && allocation.price) || variant.price || 0;
        }
      }

      console.log("[Interceptor] Amount (cents):", amountInCents);

      pendingData = {
        amountInCents:    amountInCents,
        planName:         planName,
        planSellingPlanId: planSellingPlanId,
      };

      // Pre-fill from Shopify customer session
      if (window.ShopifyCustomer) {
        document.getElementById("subs-email").value      = window.ShopifyCustomer.email      || "";
        document.getElementById("subs-first-name").value = window.ShopifyCustomer.first_name || "";
        document.getElementById("subs-last-name").value  = window.ShopifyCustomer.last_name  || "";
      }

      openModal();
    });

    console.log("[Interceptor] ✅ Fully attached to form:", form.id || form);
  });

})();